import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, MapPin, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "../lib/api";

/**
 * TIME-1 §7 — Timesheets. Flagged entries come first with their evidence; clean entries approve in
 * bulk with one click. Every approval, adjustment and resolution is logged with who, when and why.
 * The app never computes pay — an overtime or break flag is something for a human to look at.
 */
const when = (iso) => (iso ? new Date(iso).toLocaleString([], {
  month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

const STATUS_TONE = {
  Open: "bg-gray-100 text-gray-700", Submitted: "bg-blue-100 text-blue-800",
  Flagged: "bg-amber-100 text-amber-900", Approved: "bg-emerald-100 text-emerald-800",
  Pushed: "bg-slate-800 text-white", Rejected: "bg-red-100 text-red-800",
};

function Evidence({ flag }) {
  const e = flag.evidence || {};
  return (
    <div className="mt-1 text-[11px] text-gray-600" data-testid={`ts-evidence-${flag.code}`}>
      {e.minutes !== undefined && <span>{Math.round(e.minutes)} min · </span>}
      {e.radius_m !== undefined && <span>fence {Math.round(e.radius_m)} m · </span>}
      {e.clock_in && <span>in {when(e.clock_in)} · </span>}
      {e.window_start && <span>dispatched {e.window_start} · </span>}
      {e.other_entry_id && <span>vs entry {e.other_entry_id.slice(0, 8)} · </span>}
      {e.observed_project_id && <span>fence saw {e.observed_project_id.slice(0, 8)} · </span>}
      {(e.points || []).length > 0 && (
        <span>
          {e.points.length} point(s) off site, first {when(e.points[0].at)} at{" "}
          {e.points[0].lat.toFixed(4)}, {e.points[0].lng.toFixed(4)}
        </span>
      )}
      {e.observations === 0 && <span>no location points recorded</span>}
      {(e.answers || []).map((a) => (
        <div key={a.question_id} className={a.flagged ? "text-amber-900 font-medium" : ""}>
          “{a.text}” — <b>{a.answer || "(no answer)"}</b>
          {a.note ? ` · note: ${a.note}` : ""}
          {a.photo_path ? " · photo attached" : ""}
        </div>
      ))}
    </div>
  );
}

export default function TimesheetsPage() {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({});
  const [filters, setFilters] = useState({ project_id: "", user_email: "", status: "" });
  const [projects, setProjects] = useState([]);
  const [reason, setReason] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/time/entries", { params: { ...filters, limit: 300 } });
    setRows(data.rows);
    setTotals(data.totals);
  }, [filters]);

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); await load(); }
    catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.detail || "That did not go through");
    } finally { setBusy(false); }
  };

  const approveClean = () => act(
    async () => {
      const { data } = await api.post("/time/entries/approve",
                                      { ...filters, clean_only: true });
      if (!data.approved) throw new Error("none");
      toast.success(`${data.approved} clean entr${data.approved === 1 ? "y" : "ies"} approved`,
                    { description: data.skipped.length
                        ? `${data.skipped.length} flagged entr(ies) left for you to look at`
                        : undefined });
    }, "Approved");

  return (
    <div className="space-y-3" data-testid="timesheets-page">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-gray-500" data-testid="ts-totals">
          {totals.entries || 0} entries · {totals.flagged || 0} flagged · {totals.clean || 0} clean ·{" "}
          {totals.approved || 0} approved · {totals.hours || 0} h
        </span>
        <div className="ml-auto flex items-center gap-2">
          <select value={filters.project_id} data-testid="ts-filter-project"
                  onChange={(e) => setFilters((f) => ({ ...f, project_id: e.target.value }))}
                  className="h-8 text-xs border border-gray-300 rounded-md px-2 max-w-[220px]">
            <option value="">All jobs</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>{p.job_number} {p.project_name}</option>
            ))}
          </select>
          <select value={filters.status} data-testid="ts-filter-status"
                  onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
                  className="h-8 text-xs border border-gray-300 rounded-md px-2">
            <option value="">Any status</option>
            {["Open", "Submitted", "Flagged", "Approved", "Pushed", "Rejected"].map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700" disabled={busy}
                  onClick={approveClean} data-testid="ts-approve-clean">
            <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve clean
          </Button>
        </div>
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-gray-500 border border-dashed border-gray-300 rounded-lg p-6 text-center"
           data-testid="ts-empty">No time recorded for this filter.</p>
      ) : (
        <ul className="space-y-2" data-testid="ts-rows">
          {rows.map((e) => (
            <li key={e.id} data-testid={`ts-row-${e.id}`}
                className={`rounded-lg border bg-white p-3 ${
                  e.flags?.length ? "border-amber-300" : "border-gray-200"}`}>
              <div className="flex items-center gap-2 flex-wrap text-sm">
                <b className="text-gray-900">{e.employee_name || e.user_email}</b>
                <span className="text-gray-500">{e.classification}</span>
                <span className={`px-1.5 rounded text-[10px] font-semibold ${STATUS_TONE[e.status] || ""}`}
                      data-testid={`ts-status-${e.id}`}>{e.status}</span>
                {e.prevailing_wage && (
                  <span className="px-1.5 rounded text-[10px] bg-indigo-100 text-indigo-800">
                    prevailing wage
                  </span>
                )}
                <span className="ml-auto tabular-nums font-semibold text-gray-900">
                  {e.worked_hours} h
                </span>
              </div>
              <div className="text-xs text-gray-600 mt-0.5">
                {e.job_number} {e.project_name} · {e.scope_type || "—"} · {when(e.clock_in)} →{" "}
                {e.clock_out ? when(e.clock_out) : "still open"}
                {(e.breaks || []).length ? ` · ${e.breaks.length} break(s)` : " · no break"}
                {e.source !== "app" ? ` · ${e.source.replace(/_/g, " ")}` : ""}
              </div>

              {(e.flags || []).map((f) => (
                <div key={f.code} className="mt-2 rounded-md bg-amber-50 border border-amber-200 p-2"
                     data-testid={`ts-flag-${e.id}-${f.code}`}>
                  <div className="flex items-center gap-1 text-xs font-semibold text-amber-900">
                    <AlertTriangle className="w-3.5 h-3.5" /> {f.label}
                    <span className="font-normal text-amber-800">— {f.message}</span>
                  </div>
                  <Evidence flag={f} />
                  <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                    <Input value={reason[`${e.id}${f.code}`] || ""} placeholder="reason (required to adjust)"
                           onChange={(ev) => setReason((r) => ({ ...r, [`${e.id}${f.code}`]: ev.target.value }))}
                           className="h-7 text-[11px] max-w-[240px]"
                           data-testid={`ts-reason-${e.id}-${f.code}`} />
                    {[["accept", "Accept as-is"], ["adjust_to_observed", "Adjust to observed"],
                      ["adjust_manual", "Adjust by hand"], ["ask_employee", "Ask employee"]].map(
                      ([res, label]) => (
                        <Button key={res} size="sm" variant="outline" className="h-7 text-[11px]"
                                disabled={busy}
                                data-testid={`ts-resolve-${e.id}-${f.code}-${res}`}
                                onClick={() => act(() => api.post(`/time/entries/${e.id}/resolve`, {
                                  flag: f.code, resolution: res,
                                  reason: reason[`${e.id}${f.code}`] || "",
                                  note_to_employee: res === "ask_employee"
                                    ? (reason[`${e.id}${f.code}`] || "Please check this entry") : "",
                                }), label)}>
                          {label}
                        </Button>
                      ))}
                  </div>
                </div>
              ))}

              {e.status !== "Approved" && e.status !== "Pushed" && !e.flags?.length && e.clock_out && (
                <div className="mt-2">
                  <Button size="sm" className="h-7 text-[11px]" disabled={busy}
                          data-testid={`ts-approve-${e.id}`}
                          onClick={() => act(() => api.post("/time/entries/approve",
                                                            { entry_ids: [e.id] }), "Approved")}>
                    Approve
                  </Button>
                </div>
              )}
              {(e.status === "Approved" || e.status === "Pushed") && (
                <div className="mt-2 flex items-center gap-2 text-[11px] text-gray-500">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                  Approved by {e.approved_by} {when(e.approved_at)}
                  {e.status === "Approved" && (
                    <Button size="sm" variant="ghost" className="h-6 text-[11px] text-red-700"
                            disabled={busy} data-testid={`ts-reject-${e.id}`}
                            onClick={() => {
                              const note = window.prompt("What should the employee fix?");
                              if (note) act(() => api.post(`/time/entries/${e.id}/reject`, { note }),
                                            "Sent back to the employee");
                            }}>
                      <Undo2 className="w-3 h-3 mr-1" /> Send back
                    </Button>
                  )}
                </div>
              )}
              {e.rejection_note && (
                <p className="mt-1 text-[11px] text-red-800" data-testid={`ts-rejection-${e.id}`}>
                  Sent back: {e.rejection_note}
                </p>
              )}
              {(e.corrections || []).length > 0 && (
                <p className="mt-1 text-[11px] text-gray-500" data-testid={`ts-log-${e.id}`}>
                  <MapPin className="w-3 h-3 inline mr-0.5" />
                  {e.corrections.length} logged change(s) — last: {e.corrections.at(-1).actor},{" "}
                  {e.corrections.at(-1).reason}
                </p>
              )}
              {[["clockin_answers", "At clock-in"], ["clockout_answers", "At clock-out"]].map(
                ([field, label]) => ((e[field] || []).length > 0 && (
                  <div key={field} className="mt-1.5 text-[11px] text-gray-600"
                       data-testid={`ts-${field}-${e.id}`}>
                    <span className="font-semibold text-gray-700">{label}:</span>
                    {e[field].map((a) => (
                      <span key={a.question_id} className={`block pl-2 ${
                        a.flagged ? "text-amber-900 font-medium" : ""}`}>
                        “{a.text}” — {a.answer || "(no answer)"}
                        {a.note ? ` · note: ${a.note}` : ""}
                      </span>
                    ))}
                  </div>
                )))}
            </li>
          ))}
        </ul>
      )}
      <p className="text-[11px] text-gray-400">
        Hours here are what was worked. Pay — overtime, premiums, rates — is always calculated in
        Miter; this app never computes it.
      </p>
    </div>
  );
}
